import { Info } from 'lucide-react';
import { useId, type ComponentProps } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UsDateInput } from '@/components/shared/UsDateInput';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

type FormFieldProps = Omit<ComponentProps<'input'>, 'id'> & {
  readonly label: string;
  /** Message shown under the field. Its presence is what marks the field invalid. */
  readonly error?: string | null;
  /**
   * Id of guidance rendered outside this component that still has to be announced with the field.
   * It stays outside because a block of help text inside the field would grow this cell alone, and in
   * a grid row of fields that pushes this input out of line with its neighbours.
   */
  readonly describedById?: string;
  /** The same kind of guidance on demand, from an info control beside the label. */
  readonly tooltip?: string;
  /** Receives the ISO date of a `type="date"` field, which is shown and typed as MM/DD/YYYY. */
  onDateChange?: (isoDate: string) => void;
};

/**
 * A labelled text input. The label is bound to the input by a generated id, and the hint and error are
 * bound to it by `aria-describedby`, so the field's validity and its explanation are always announced
 * together — a WCAG 2.1 AA requirement enforced by axe in CI.
 */
export function FormField({
  label,
  error = null,
  describedById,
  tooltip,
  onDateChange,
  ...inputProps
}: FormFieldProps): React.JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;

  // Both when both are present: the guidance is what tells the provider how to fix what the error
  // reports, so hearing only one of them is the case worth avoiding.
  const describedBy = [describedById ?? null, error === null ? null : errorId]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id}>{label}</Label>
        {tooltip !== undefined && <FieldTooltip text={tooltip} />}
      </div>

      {inputProps.type === 'date' && onDateChange !== undefined ? (
        <UsDateInput
          id={id}
          name={inputProps.name}
          required={inputProps.required}
          aria-invalid={error !== null}
          aria-describedby={describedBy.length === 0 ? undefined : describedBy}
          value={typeof inputProps.value === 'string' ? inputProps.value : ''}
          onValueChange={onDateChange}
          reportInvalidText
          disabled={inputProps.disabled}
          readOnly={inputProps.readOnly}
          onBlur={inputProps.onBlur}
        />
      ) : (
        <Input
          id={id}
          aria-invalid={error !== null}
          aria-describedby={describedBy.length === 0 ? undefined : describedBy}
          {...inputProps}
        />
      )}

      {error !== null && (
        <p id={errorId} role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * `type="button"` is load-bearing: the control sits inside a form, and a default submit button here
 * would run the search every time someone asked what the field wanted.
 *
 * The name deliberately leaves the field's own label out. Repeating it — "About Policy Number" — gives
 * every `getByLabel('Policy Number')` two matches, which is an ambiguous query in both Testing Library
 * and Playwright. Nothing is lost by omitting it: the same guidance is already in the field's
 * `aria-describedby`, so a screen reader hears it with the field whether this control is used or not.
 */
function FieldTooltip({ text }: { readonly text: string }): React.JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Show format guidance"
          // Negative margin against the padding: a 24px hit area for touch and for imprecise pointers,
          // without the icon itself moving or the heading row growing.
          className="text-muted hover:text-brand-600 -m-1.5 inline-flex items-center justify-center p-1.5"
        >
          <Info aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}
